/**
 * TeamTab — Collaborator management for admin_agence
 * List, invite, edit access, remove team members
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Switch,
  CircularProgress,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import LockResetIcon from '@mui/icons-material/LockReset';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { motion, AnimatePresence } from 'framer-motion';
import { agencyAPI } from '../../services/api';
import { colors } from '../../styles/colors';
import ConfirmDialog from '../shared/ConfirmDialog';
import InviteDialog from './InviteDialog';

const ALL_PAGES = ['radar', 'comprendre', 'agir', 'mesurer'];
const PAGE_LABELS = { radar: 'Radar', comprendre: 'Comprendre', agir: 'Agir', mesurer: 'Mesurer' };

const ROLE_CHIP_STYLES = {
  admin_agence: { bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#D97706', label: 'Admin' },
  manager: { bgcolor: colors.primaryBg, color: colors.primary, label: 'Manager' },
  viewer: { bgcolor: colors.infoBg, color: colors.info, label: 'Viewer' },
};

export default function TeamTab({ brands, brandPlans }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [editingAccess, setEditingAccess] = useState({}); // { userId: { brandId: [pages] } }
  const [savingAccess, setSavingAccess] = useState(null);
  const [editingRole, setEditingRole] = useState(null); // userId being role-edited
  const [resetPwdResult, setResetPwdResult] = useState(null);
  const [resetCopied, setResetCopied] = useState(false);

  // Current user info to identify self
  const currentUserIdRef = useRef(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await agencyAPI.getUsers();
      const data = res.data || res;
      setUsers(Array.isArray(data) ? data : []);

      // Identify current user via /api/auth/me
      try {
        const meRes = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/auth/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (meRes.ok) {
          const me = await meRes.json();
          currentUserIdRef.current = me.id;
        }
      } catch { /* silent */ }
    } catch (err) {
      console.error('Fetch team error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Count stats
  const adminCount = users.filter(u => u.role === 'admin_agence').length;
  const managerCount = users.filter(u => u.role === 'manager').length;
  const viewerCount = users.filter(u => u.role === 'viewer').length;

  const handleInvite = async (data, accessList) => {
    const res = await agencyAPI.inviteUser(data);
    const result = res.data || res;

    // Now set per-brand access if multiple brands
    if (accessList && accessList.length > 0) {
      try {
        await agencyAPI.setUserBrands(result.user_id, accessList.map(a => ({
          brand_id: a.brand_id,
          pages_access: a.pages_access,
        })));
      } catch { /* access set during invite already covers single pages_access */ }
    }

    await fetchUsers();
    return result;
  };

  const handleRemoveUser = (user) => {
    setConfirmDialog({
      open: true,
      title: 'Supprimer ce collaborateur',
      message: `Etes-vous sur de vouloir retirer ${user.full_name || user.email} de votre equipe ? Il perdra tous ses acces.`,
      onConfirm: async () => {
        try {
          await agencyAPI.removeUser(user.user_id);
          setSnackbar({ open: true, message: 'Collaborateur retire', severity: 'success' });
          await fetchUsers();
        } catch (err) {
          const msg = err?.response?.data?.detail || 'Erreur lors de la suppression';
          setSnackbar({ open: true, message: msg, severity: 'error' });
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await agencyAPI.updateUserRole(userId, newRole);
      setSnackbar({ open: true, message: 'Role mis a jour', severity: 'success' });
      setEditingRole(null);
      await fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur lors du changement de role';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  // Initialize editing access state when expanding
  const handleExpand = (userId) => {
    if (expanded === userId) {
      setExpanded(null);
      return;
    }
    setExpanded(userId);
    const user = users.find(u => u.user_id === userId);
    if (user && user.role !== 'admin_agence') {
      const accessMap = {};
      (user.brand_access || []).forEach(ba => {
        accessMap[ba.brand_id] = ba.pages_access || [];
      });
      setEditingAccess(prev => ({ ...prev, [userId]: accessMap }));
    }
  };

  const handleTogglePageAccess = (userId, brandId, page) => {
    setEditingAccess(prev => {
      const userAccess = { ...(prev[userId] || {}) };
      const pages = userAccess[brandId] || [];
      if (pages.includes(page)) {
        userAccess[brandId] = pages.filter(p => p !== page);
        if (userAccess[brandId].length === 0) delete userAccess[brandId];
      } else {
        userAccess[brandId] = [...pages, page];
      }
      return { ...prev, [userId]: userAccess };
    });
  };

  const handleSaveAccess = async (userId) => {
    setSavingAccess(userId);
    try {
      const userAccess = editingAccess[userId] || {};
      const access = Object.entries(userAccess).map(([brandId, pages]) => ({
        brand_id: parseInt(brandId),
        pages_access: pages,
      }));
      await agencyAPI.setUserBrands(userId, access);
      setSnackbar({ open: true, message: 'Acces mis a jour', severity: 'success' });
      await fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur lors de la mise a jour';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSavingAccess(null);
    }
  };

  const handleResetPassword = async (user) => {
    setConfirmDialog({
      open: true,
      title: 'Reinitialiser le mot de passe',
      message: `Generer un nouveau mot de passe temporaire pour ${user.full_name || user.email} ? L'ancien sera invalide immediatement.`,
      onConfirm: async () => {
        try {
          const res = await agencyAPI.resetPassword(user.user_id);
          const data = res.data || res;
          setResetPwdResult({ ...data, full_name: user.full_name, email: user.email });
        } catch (err) {
          const msg = err?.response?.data?.detail || 'Erreur lors de la reinitialisation';
          setSnackbar({ open: true, message: msg, severity: 'error' });
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  const handleCopyResetPwd = async () => {
    if (!resetPwdResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(resetPwdResult.temp_password);
      setResetCopied(true);
      setTimeout(() => setResetCopied(false), 2000);
    } catch { /* silent */ }
  };

  // Get allowed pages for a brand from brandPlans
  const getAllowedPages = (brandId) => {
    const plan = brandPlans?.find(bp => bp.brand_id === brandId);
    if (!plan?.pages || plan.pages.length === 0) return ALL_PAGES;
    return plan.pages;
  };

  const getInitials = (name, email) => {
    if (name) {
      const parts = name.split(' ').filter(Boolean);
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    }
    return (email || '??').substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return ''; }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} sx={{ color: colors.primary }} />
      </Box>
    );
  }

  // Sort: admins first, then by name
  const roleOrder = { admin_agence: 0, manager: 1, viewer: 2 };
  const sortedUsers = [...users].sort((a, b) => {
    const ro = (roleOrder[a.role] || 9) - (roleOrder[b.role] || 9);
    if (ro !== 0) return ro;
    return (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '');
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, fontSize: '1.3rem', color: colors.charcoal }}>
            Mon equipe
          </Typography>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textMed, mt: 0.5 }}>
            Gerez les collaborateurs et leurs acces aux marques
          </Typography>
        </Box>
        {users.length > 1 && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteOpen(true)}
            disabled={brands.length === 0}
            sx={{
              borderRadius: '100px',
              textTransform: 'none',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              fontSize: '0.85rem',
              bgcolor: colors.primary,
              color: colors.white,
              px: 3, py: 1,
              boxShadow: 'none',
              '&:hover': { bgcolor: colors.primaryLight, boxShadow: '0 4px 16px rgba(27,94,64,0.15)' },
            }}
          >
            Inviter
          </Button>
        )}
      </Box>

      {/* KPI strip */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        {[
          { label: 'MEMBRES ACTIFS', value: users.length, sub: `${users.length} utilisateur${users.length > 1 ? 's' : ''}`, color: colors.charcoal },
          { label: 'MANAGERS', value: managerCount, sub: 'acces creation actions', color: colors.primary },
          { label: 'VIEWERS', value: viewerCount, sub: 'acces lecture seule', color: colors.info },
        ].map((kpi, i) => (
          <Box
            key={i}
            sx={{
              background: colors.cardGlass,
              backdropFilter: 'blur(16px)',
              border: `1px solid ${colors.borderLight}`,
              borderRadius: '16px',
              p: '18px 22px',
            }}
          >
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textMed, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, mb: 0.5 }}>
              {kpi.label}
            </Typography>
            <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.8rem', fontWeight: 500, color: kpi.color, lineHeight: 1.2 }}>
              {kpi.value}
            </Typography>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem', color: colors.textLight }}>
              {kpi.sub}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Empty state — show when alone (only admin, no collaborators) */}
      {users.length <= 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
          <GroupIcon sx={{ fontSize: 56, color: colors.textLight, opacity: 0.5 }} />
          <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.1rem', fontWeight: 500, color: colors.charcoal }}>
            {users.length === 0 ? 'Aucun collaborateur' : 'Invitez votre premier collaborateur'}
          </Typography>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textMed, textAlign: 'center', maxWidth: 360 }}>
            Invitez des membres de votre equipe pour collaborer sur la gestion de vos marques.
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteOpen(true)}
            disabled={brands.length === 0}
            sx={{
              mt: 1,
              borderRadius: '100px',
              textTransform: 'none',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              bgcolor: colors.primary,
              '&:hover': { bgcolor: colors.primaryLight },
            }}
          >
            Inviter un collaborateur
          </Button>
        </Box>
      )}

      {/* Users list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sortedUsers.map((user, index) => {
          const isExpanded = expanded === user.user_id;
          const isSelf = user.user_id === currentUserIdRef.current;
          const isAdmin = user.role === 'admin_agence';
          const isOnlyAdmin = isAdmin && adminCount <= 1;
          const chipStyle = ROLE_CHIP_STYLES[user.role] || ROLE_CHIP_STYLES.viewer;
          const brandAccessCount = user.brand_access?.length || 0;

          return (
            <motion.div
              key={user.user_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.06 * index, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <CollaboratorCard
                user={user}
                isExpanded={isExpanded}
                isSelf={isSelf}
                isAdmin={isAdmin}
                isOnlyAdmin={isOnlyAdmin}
                chipStyle={chipStyle}
                brandAccessCount={brandAccessCount}
                isMobile={isMobile}
                onExpand={() => handleExpand(user.user_id)}
                onRemove={() => handleRemoveUser(user)}
                onResetPassword={() => handleResetPassword(user)}
                editingRole={editingRole}
                onStartEditRole={() => setEditingRole(user.user_id)}
                onRoleChange={(newRole) => handleRoleChange(user.user_id, newRole)}
                onCancelEditRole={() => setEditingRole(null)}
                adminCount={adminCount}
                formatDate={formatDate}
                getInitials={getInitials}
              >
                {/* Expanded content: brand access table */}
                {isAdmin ? (
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textMed, fontStyle: 'italic' }}>
                    Un admin agence a acces a toutes les marques.
                  </Typography>
                ) : (
                  <Box>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', fontWeight: 600, color: colors.charcoal, letterSpacing: '0.04em', mb: 1.5 }}>
                      Acces par marque
                    </Typography>

                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            {['MARQUE', 'RADAR', 'COMPRENDRE', 'AGIR', 'MESURER'].map(h => (
                              <th key={h} style={{
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '0.72rem',
                                color: colors.textMed,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                padding: '10px 12px',
                                borderBottom: `1px solid ${colors.borderLight}`,
                                textAlign: h === 'MARQUE' ? 'left' : 'center',
                                whiteSpace: 'nowrap',
                              }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {brands.map((brand, bi) => {
                            const userAccess = editingAccess[user.user_id] || {};
                            const brandPages = userAccess[brand.id] || [];
                            const allowedPages = getAllowedPages(brand.id);

                            return (
                              <tr key={brand.id} style={{ borderBottom: bi === brands.length - 1 ? 'none' : `1px solid ${colors.borderLight}` }}>
                                <td style={{ padding: '12px', fontFamily: '"DM Sans", sans-serif', fontSize: '0.8rem', fontWeight: 600, color: colors.charcoal }}>
                                  {brand.name}
                                </td>
                                {ALL_PAGES.map(page => {
                                  const allowed = allowedPages.includes(page);
                                  const active = brandPages.includes(page);
                                  return (
                                    <td key={page} style={{ padding: '12px', textAlign: 'center' }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Switch
                                          size="small"
                                          checked={active}
                                          disabled={!allowed}
                                          onChange={() => handleTogglePageAccess(user.user_id, brand.id, page)}
                                          aria-label={`Acces ${PAGE_LABELS[page]} pour ${brand.name}`}
                                          sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: colors.primary },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.primary },
                                          }}
                                        />
                                      </Box>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleSaveAccess(user.user_id)}
                        disabled={savingAccess === user.user_id}
                        sx={{
                          borderRadius: '100px',
                          textTransform: 'none',
                          fontFamily: '"DM Sans", sans-serif',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          color: colors.primary,
                          borderColor: colors.primaryLight,
                          '&:hover': { bgcolor: colors.primaryBg },
                        }}
                      >
                        {savingAccess === user.user_id ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                        Enregistrer les acces
                      </Button>
                    </Box>
                  </Box>
                )}
              </CollaboratorCard>
            </motion.div>
          );
        })}
      </Box>

      {/* Invite Dialog */}
      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        brands={brands}
        brandPlans={brandPlans}
        onInvite={handleInvite}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        severity="warning"
      />

      {/* Reset password result dialog */}
      {resetPwdResult && (
        <Box
          onClick={() => { setResetPwdResult(null); setResetCopied(false); }}
          sx={{ position: 'fixed', inset: 0, zIndex: 1300, bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box onClick={e => e.stopPropagation()} sx={{ bgcolor: colors.white, borderRadius: '20px', p: 4, maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.1rem', fontWeight: 500, color: colors.charcoal, mb: 2, textAlign: 'center' }}>
              Nouveau mot de passe
            </Typography>
            <Box sx={{ background: colors.ivory, borderRadius: '12px', border: `1px solid ${colors.borderLight}`, p: '18px 22px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textMed, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                  UTILISATEUR
                </Typography>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.charcoal }}>
                  {resetPwdResult.full_name || resetPwdResult.email}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textMed, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                  MOT DE PASSE
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontFamily: '"SF Mono", "Fira Code", "Courier New", monospace', fontSize: '0.95rem', letterSpacing: '0.05em', color: colors.charcoal, fontWeight: 600 }}>
                    {resetPwdResult.temp_password}
                  </Typography>
                  <Tooltip title={resetCopied ? 'Copie !' : 'Copier'}>
                    <IconButton size="small" onClick={handleCopyResetPwd} sx={{ color: colors.textMed, '&:hover': { color: colors.primary } }}>
                      <ContentCopyIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
            <Alert severity="warning" sx={{ mt: 2, borderRadius: '12px', fontSize: '0.8rem' }}>
              Ce mot de passe ne sera plus visible apres fermeture.
            </Alert>
            <Button
              variant="contained"
              fullWidth
              onClick={() => { setResetPwdResult(null); setResetCopied(false); }}
              sx={{ mt: 2, borderRadius: '100px', textTransform: 'none', fontFamily: '"DM Sans", sans-serif', fontWeight: 600, bgcolor: colors.primary, '&:hover': { bgcolor: colors.primaryLight } }}
            >
              Fermer
            </Button>
          </Box>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ borderRadius: '12px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


// --- CollaboratorCard sub-component ---
function CollaboratorCard({
  user, isExpanded, isSelf, isAdmin, isOnlyAdmin, chipStyle,
  brandAccessCount, isMobile, onExpand, onRemove, onResetPassword,
  editingRole, onStartEditRole, onRoleChange, onCancelEditRole,
  adminCount, formatDate, getInitials, children,
}) {
  const cardRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const canDelete = !isSelf || !isOnlyAdmin;
  const canEditRole = !isOnlyAdmin || !isAdmin;

  return (
    <Box
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onClick={onExpand}
      sx={{
        position: 'relative',
        borderRadius: '20px',
        border: `1px solid ${colors.borderLight}`,
        background: colors.cardGlass,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        p: '22px 26px',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 12px 40px rgba(0,0,0,0.06)',
          transform: 'translateY(-2px)',
          borderColor: colors.primaryLight,
        },
        '&:focus-visible': {
          outline: `2px solid ${colors.primary}`,
          outlineOffset: '2px',
        },
      }}
      tabIndex={0}
      role="button"
      aria-expanded={isExpanded}
      aria-label={`Collaborateur ${user.full_name || user.email}, role ${user.role}, ${brandAccessCount} marques`}
    >
      {/* Mouse glow */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(250px circle at ${mousePos.x}px ${mousePos.y}px, rgba(27,94,64,0.07), transparent 60%)`,
          pointerEvents: 'none',
          zIndex: 0,
          borderRadius: 'inherit',
        }}
      />

      {/* Collapsed content */}
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        {/* Left: avatar + name */}
        <Box sx={{ flex: '1 1 200px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%',
            bgcolor: colors.primaryBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', fontWeight: 700,
            color: colors.primary, textTransform: 'uppercase', flexShrink: 0,
          }}>
            {getInitials(user.full_name, user.email)}
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', fontWeight: 600, color: colors.charcoal, lineHeight: 1.3 }}>
                {user.full_name || user.email}
              </Typography>
              {isSelf && (
                <Chip label="Vous" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: colors.primaryBg, color: colors.primary, borderRadius: '100px' }} />
              )}
            </Box>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem', fontWeight: 400, color: colors.textLight, lineHeight: 1.3 }}>
              {user.email}
            </Typography>
          </Box>
        </Box>

        {/* Right: role chip, brand count, actions, chevron */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Role chip or role editor */}
          {editingRole === user.user_id ? (
            <Select
              value={user.role}
              size="small"
              onClick={e => e.stopPropagation()}
              onChange={e => { e.stopPropagation(); onRoleChange(e.target.value); }}
              onClose={e => { e.stopPropagation(); onCancelEditRole(); }}
              sx={{ fontSize: '0.78rem', fontFamily: '"DM Sans", sans-serif', minWidth: 120, '& .MuiOutlinedInput-notchedOutline': { borderRadius: '100px' } }}
            >
              <MenuItem value="admin_agence">Admin</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="viewer">Viewer</MenuItem>
            </Select>
          ) : (
            <Chip
              label={chipStyle.label}
              onClick={canEditRole && !isSelf ? (e) => { e.stopPropagation(); onStartEditRole(); } : undefined}
              sx={{
                bgcolor: chipStyle.bgcolor,
                color: chipStyle.color,
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 600,
                borderRadius: '100px',
                fontFamily: '"DM Sans", sans-serif',
                cursor: canEditRole && !isSelf ? 'pointer' : 'default',
                '&:hover': canEditRole && !isSelf ? { opacity: 0.8 } : {},
              }}
            />
          )}

          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: colors.textMed }}>
            {isAdmin ? 'Toutes les marques' : `${brandAccessCount} marque${brandAccessCount !== 1 ? 's' : ''}`}
          </Typography>

          {/* Password display */}
          <Typography sx={{
            fontFamily: '"SF Mono", "Fira Code", "Courier New", monospace',
            fontSize: '0.78rem',
            letterSpacing: '0.05em',
            color: user.initial_password ? colors.charcoal : colors.textLight,
          }}>
            {user.initial_password || '--'}
          </Typography>

          {!isMobile && (
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textLight }}>
              {user.created_at ? `Ajoute le ${formatDate(user.created_at)}` : ''}
            </Typography>
          )}

          {/* Action buttons (not for self if only admin) */}
          {!isSelf && (
            <Box sx={{ display: 'flex', gap: 0.5 }} onClick={e => e.stopPropagation()}>
              <Tooltip title="Reinitialiser le mot de passe">
                <IconButton
                  size="small"
                  onClick={() => onResetPassword()}
                  aria-label={`Reinitialiser mot de passe de ${user.full_name || user.email}`}
                  sx={{ color: colors.textMed, '&:hover': { color: colors.primary, bgcolor: colors.primaryBg } }}
                >
                  <LockResetIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {canDelete && (
                <Tooltip title="Supprimer">
                  <IconButton
                    size="small"
                    onClick={() => onRemove()}
                    aria-label={`Supprimer ${user.full_name || user.email}`}
                    sx={{ color: colors.textLight, '&:hover': { color: colors.danger, bgcolor: colors.dangerBg } }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          <ExpandMoreIcon sx={{
            fontSize: 20,
            color: colors.textMed,
            transition: 'transform 0.3s ease',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }} />
        </Box>
      </Box>

      {/* Expanded panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.borderLight}`, position: 'relative', zIndex: 1 }} onClick={e => e.stopPropagation()}>
              {children}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
